import { useEffect, useState } from 'react';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { Base } from 'components/Containers/BaseContainer';
import { ToolbarContainer } from 'components/Containers/ToolbarContainer';
import { FlexBox } from 'components/Containers/FlexBox';
import { Selector } from 'components/Form/Selectors/Selector';
import { NotificationsList } from './components/List';
import { filterNotifications } from './utils';
import NotificationHandler from 'handlers/notification.handler';
import { ConfirmClearNotificationModal } from './components/Modal/ConfirmationModal';
import { isDarkTheme } from 'utils/theme';
import { ThemePalette } from 'mui.theme';
import LineChartComponent from 'components/Charts/LineChart';
import { type TagDetails, type TagAlertDetails } from 'types/tag.type';
import TagHandler from 'handlers/tag.handler';
import { formatChartData } from 'utils/helpers/chartData';
import { setIsLoading } from 'store/notification.slice';
import {
  type RuleCondition,
  type RuleNotifications,
} from 'types/notification.types';
import {
  clearSelectedNotifications,
  setSelectedNotifications,
} from 'store/ruleNotification.slice';
import NoChartData from 'views/FuelAnalytics/NoChart';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { useLocation } from 'react-router-dom';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _palette = clientThemePalette[CLIENT_THEME];

enum OpenModal {
  None,
  ConfirmClearNotification,
}

export const NotificationsView = (): JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();
  const bumperNumberReceived = location.state?.data;

  const notificationHandler = new NotificationHandler();
  const tagHandler = new TagHandler();
  // Get the list of notifications from redux
  const { notifications, selectedNotification } = useAppSelector(
    (state) => state.ruleNotificationsReducer
  );
  const { rules } = useAppSelector((state) => state.ruleReducer);
  const dispatch = useAppDispatch();

  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();

  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const { assets } = useAppSelector((state) => state.assetReducer);
  const enableDarkTheme = isDarkTheme(theme);
  const [selectedViewOption, setSelectedViewOption] =
    useState<string>('active');
  const [selectedClearOption, setSelectedClearOption] = useState<string>('');
  const { isLoading } = useAppSelector((state) => state.appReducer);
  const [modalOpen, setModalOpen] = useState(OpenModal.None);
  const selectorOptions = [
    {
      id: 'all',
      display: t('all'),
    },
    {
      id: 'active',
      display: t('active'),
    },
    {
      id: 'inactive',
      display: t('inactive'),
    },
  ];
  const clearSelectorOptions = [
    {
      id: 'today',
      display: t('notif_clear_today'),
    },
    {
      id: 'yesterday',
      display: t('notif_clear_yesterday'),
    },
    {
      id: 'thisWeek',
      display: t('notif_clear_this_week'),
    },
    {
      id: 'lastWeek',
      display: t('notif_clear_last_week'),
    },
    {
      id: 'all',
      display: t('notif_clear_all'),
    },
  ];

  const handleClearNotifications = async () => {
    const notificationsToDelete = filterNotifications(
      selectedClearOption,
      notifications
    );
    const notificationIds = notificationsToDelete.map((item) => {
      return item.notificationId;
    });
    if (notificationIds.length > 0) {
      await notificationHandler.clearNotifications(notificationIds);
    }
    setSelectedClearOption('');
  };

  const [alertDetails, setAlertDetails] = useState<TagAlertDetails>({
    deviceId: '',
    notificationTime: '',
    tagDetails: [{ tagName: '', operator: '', value: 0 }],
  });

  const [chartData, setChartData] = useState<any>();
  const [formattedData, setFormattedData] = useState<any>();
  const [lineChartUnit, setLineChartUnit] = useState<string>('unit');
  const [selectedRow, setSelectedRow] = useState<string>('');
  const [selectedRowNotification, setSelectedRowNotification] =
    useState<RuleNotifications>();

  const [isReloaded, setIsReloaded] = useState(false);
  const [filteredNotification, setFilteredNotification] = useState<
    RuleNotifications[]
  >(notifications.filter((item) => item.isActive));

  const handleAlertDetails = (data: TagAlertDetails) => {
    setAlertDetails(data);
  };

  const handleSelectedNotification = (notificationId: string) => {
    const notification = notifications.find(
      (item) => item.notificationId === notificationId
    );
    setSelectedRowNotification(notification);
    return notification;
  };

  const getAlertDataPoints = async () => {
    const data = await tagHandler.getNotificationTagDetails(alertDetails);
    setChartData(data);
    if (data && alertDetails.notificationId) {
      const selectedNotificationItem = handleSelectedNotification(
        alertDetails.notificationId
      );
      dispatch(setSelectedNotifications(selectedNotificationItem));
    } else {
      dispatch(clearSelectedNotifications());
    }
  };

  useEffect(() => {
    void getAlertDataPoints();
  }, []);

  useEffect(() => {
    void getAlertDataPoints();
  }, [alertDetails]);

  useEffect(() => {
    const dataFetch = async () => {
      if (
        chartData &&
        typeof chartData === 'object' &&
        Object.keys(chartData).length !== 0
      ) {
        const dataObject = Object.entries(chartData).map(([key, value]) => ({
          [key]: value,
        }));

        const formatData = dataObject.map((data: any) => {
          const dataName = Object.keys(data)[0];
          const dataValues = data[dataName];

          if (Array.isArray(dataValues) && dataValues.length > 0) {
            const isSymStart = dataName === 'sym_start';
            const asset = assets.find((item) => {
              const dataDeviceId = dataValues[0]?.device_sensor?.split('-')[0];
              return item.device.deviceId === dataDeviceId;
            });
            return {
              name: dataName,
              data: formatChartData(dataValues, isSymStart, dataName, asset),
              label: '',
            };
          } else {
            return {
              name: dataName,
              data: [],
              label: '',
            };
          }
        });
        setFormattedData(formatData);
      } else {
        setFormattedData([]);
      }
      setIsLoading(false);
    };
    void dataFetch();
  }, [chartData, filteredNotification, selectedViewOption]);

  useEffect(() => {
    const baseFilteredNotifications = notifications.filter(
      (ntfc: RuleNotifications) =>
        !(
          ntfc.notificationType === 'internal' &&
          ntfc.featureType === 'ml-insights'
        )
    );

    let notificationSpread = baseFilteredNotifications;
    if (bumperNumberReceived?.bumperNumber) {
      notificationSpread = baseFilteredNotifications.filter(
        (ntfc: RuleNotifications) =>
          ntfc.asset.bumperNumber === bumperNumberReceived.bumperNumber &&
          ntfc.isActive
      );
    }
    let notificationsWithFilter: any = notificationSpread;
    if (notificationSpread.length > 0) {
      if (selectedViewOption === 'active') {
        notificationsWithFilter = notificationSpread.filter(
          (ntfc: RuleNotifications) => ntfc.isActive
        );
      } else if (selectedViewOption === 'inactive') {
        notificationsWithFilter = notificationSpread.filter(
          (ntfc: RuleNotifications) => !ntfc.isActive
        );
      } else {
        notificationsWithFilter = notificationSpread;
      }

      setFilteredNotification(notificationsWithFilter);
    }

    const firstItemId = notificationsWithFilter[0]?.notificationId;
    setSelectedRow(firstItemId);

    const getAlertDataPoints = async () => {
      setIsLoading(true);
      const notification = handleSelectedNotification(firstItemId);
      if (!notification) {
        setIsLoading(false);
        return null;
      }

      const rule = rules.find((item) => item.ruleId === notification?.ruleId);
      if (!notification || !rule) {
        setIsLoading(false);
        return null;
      }

      const tagDetails: TagDetails[] = [];
      rule.conditions.map((item) => {
        tagDetails.push({
          tagName: item.tagName,
          operator: item.relationalOperator,
          value: item.value,
        });
        return null;
      });
      handleNotificationConditions(rule.conditions as any);
      const data = await tagHandler.getNotificationTagDetails({
        deviceId: notification?.deviceId,
        notificationTime: notification?.alertStartTs,
        tagDetails,
      });
      setChartData(data);

      if (data) {
        dispatch(setSelectedNotifications(notification));
      } else {
        dispatch(clearSelectedNotifications());
        setIsLoading(false);
      }
    };
    void getAlertDataPoints();
  }, [notifications, selectedViewOption, bumperNumberReceived?.bumperNumber]);

  useEffect(() => {
    dispatch(clearSelectedNotifications());
    setIsReloaded(true);
  }, []);

  const [notificationConditions, setNotificationConditions] = useState<
    RuleCondition[]
  >([]);

  const handleNotificationConditions = (conditions: []) => {
    setNotificationConditions(conditions);
  };

  return (
    <>
      <Base enableDarkTheme={enableDarkTheme}>
        {
          // Unink device confirmation modal
          modalOpen === OpenModal.ConfirmClearNotification ? (
            <ConfirmClearNotificationModal
              key={`${modalOpen.toString()}-clear-notification`}
              open={modalOpen === OpenModal.ConfirmClearNotification}
              handleAbort={() => {
                setModalOpen(OpenModal.None);
              }}
              handleSuccess={() => {
                void handleClearNotifications();
              }}
            />
          ) : (
            <></>
          )
        }
        <ToolbarContainer enableDarkTheme={enableDarkTheme}>
          <FlexBox
            sx={{
              width: '100%',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <FlexBox>
              <Typography>{t('currently_viewing')}</Typography>
              <Selector
                value={selectedViewOption}
                onChange={(event) => {
                  const selectedValue = event.target.value as string;
                  setSelectedViewOption(selectedValue);
                  setSelectedRowNotification(undefined);
                }}
                selectorOptions={selectorOptions}
                selectSx={{
                  ml: '10px',
                  flex: 1,
                  fontSize: '14px',
                }}
                enableDarkTheme={enableDarkTheme}
              />
            </FlexBox>
            <FlexBox
              sx={{
                display: 'flex',
                alignItems: 'center',
                float: 'right',
                marginRight: '20px',
              }}
            >
              <Selector
                fallbackValue={t('notif_select_bulk_clear_action')}
                value={selectedClearOption}
                onChange={(event) => {
                  const selectedValue = event.target.value as string;
                  setSelectedClearOption(selectedValue);
                }}
                selectorOptions={clearSelectorOptions}
                selectSx={{
                  ml: '10px',
                  flex: 1,
                  fontSize: '13px',
                }}
                enableDarkTheme={enableDarkTheme}
              />
              <Button
                variant="contained"
                disabled={!selectedClearOption}
                sx={{
                  marginLeft: '5px',
                  backgroundColor: 'button.primary',
                }}
                onClick={() => {
                  setModalOpen(OpenModal.ConfirmClearNotification);
                }}
              >
                {t('go_button')}
              </Button>
            </FlexBox>
          </FlexBox>
        </ToolbarContainer>
        {isLoading && filteredNotification.length < 1 ? (
          <div style={{ marginLeft: '-200px' }}>
            {' '}
            <SingleFadeLoader />
          </div>
        ) : (
          <></>
        )}
        <NotificationsList
          notifications={filteredNotification}
          handleAlertDetails={handleAlertDetails}
          handleNotificationConditions={handleNotificationConditions}
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          enableDarkTheme={enableDarkTheme}
        />
      </Base>
      <Box
        sx={{
          height: '800px',
          maxHeight: '800px',
          width: '100%',
          backgroundColor: `${
            enableDarkTheme
              ? _palette.dark.boxBackground
              : _palette.light.boxBackground
          }`,
        }}
      >
        <>
          {filteredNotification.length < 1 ? (
            <></>
          ) : isLoading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                marginTop: '-150px',
              }}
            >
              <SingleFadeLoader isFullScreen={false} />
            </div>
          ) : formattedData?.length > 0 ? (
            <>
              <Typography
                sx={{
                  textAlign: 'center',
                  fontSize: '16px',
                  fontWeight: '600',
                  paddingTop: '20px',
                  color: `${
                    enableDarkTheme
                      ? ThemePalette.typography.wheat
                      : ThemePalette.typography.black
                  }`,
                }}
              >
                {t('time_series_chart')}
                <br />
                <span
                  style={{
                    color: `${
                      enableDarkTheme
                        ? ThemePalette.typography.wheat
                        : ThemePalette.typography.black
                    }`,
                    fontStyle: 'italic',
                    fontSize: '14px',
                  }}
                >
                  {t('notification')}{' '}
                </span>
                <span
                  style={{
                    color: enableDarkTheme
                      ? _palette.dark.primary
                      : _palette.light.primary,
                    fontStyle: 'italic',
                  }}
                >
                  {selectedRowNotification?.rule?.ruleName}{' '}
                </span>
                <span
                  style={{
                    color: `${
                      enableDarkTheme
                        ? ThemePalette.typography.wheat
                        : ThemePalette.typography.black
                    }`,
                    fontStyle: 'italic',
                    fontSize: '14px',
                  }}
                >
                  {t('on')}{' '}
                </span>
                <span
                  style={{
                    color: enableDarkTheme
                      ? _palette.dark.primary
                      : _palette.light.primary,
                    fontStyle: 'italic',
                  }}
                >
                  {selectedRowNotification?.asset?.bumperNumber}{' '}
                </span>
                <span
                  style={{
                    color: `${
                      enableDarkTheme
                        ? ThemePalette.typography.wheat
                        : ThemePalette.typography.black
                    }`,
                    fontStyle: 'italic',
                    fontSize: '14px',
                  }}
                >
                  {' '}
                  {selectedRowNotification?.alertStartTs
                    ? convertUtcToDisplayTime(
                        selectedRowNotification?.alertStartTs,
                        preferredTimeDisplayMode,
                        timezone,
                        'MM/DD/YYYY h:mma'
                      )
                    : ''}{' '}
                </span>
              </Typography>
              <LineChartComponent
                xAxis="ts"
                data={formattedData}
                exportTo={false}
                handleExport={() => {}}
                assetBumperNumber={''}
                selectedRange={''}
                device={''}
                avgVal={'value'}
                enableDarkTheme={enableDarkTheme}
                xAxisTimeFormat="HH:mm"
                domainValue={[true, 'value']}
                notificationConditions={notificationConditions}
              />
            </>
          ) : (
            <NoChartData
              message={t('no_chart_data_available_for_this_notification')}
              enableDarkTheme={enableDarkTheme}
            />
          )}
        </>
      </Box>
    </>
  );
};
